import Swal from "sweetalert2";
import { loadFromLocalStorage } from "../../Common/Validation/LocalStorage";
import ProfileServices from "../../Services/ProfileServices";
import React from "react";
import { Box, Card, CardContent, Chip, Typography } from "@mui/material";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { amountFormat } from "../../Components/helper";
import Grid from '@mui/material/Grid2';

/**
 * Interface for Eligible Plans For User
 */
export interface EligiblePlansForUser {
    planId: string;
    planName: string;
    isLLACheckNeeded: boolean,
    totalDebt: number,
    totalInterest: number,
    totalEmiAmount: number,
    totalEmiAmountWithAccruals: number,
    totalEmiMonths: string,
    amountPayable: number;
    startDate: string | null,
    endDate: string | null,
    emiOfEachAssessments: AssessmentEMI[];
    emiOfAccruals: AccrualsEMI;
    interestDetail?: InterestDetail;
    penaltyDetails: PenaltyDetails,
    isJointly: boolean;
    paymentType: string;
    isEligible: boolean;
    infoUrl: string;
    recommendation: string;
    description: string;
    details: string;
    months: string;
    totalOfferAmount: number;
    offerOption: string;
    disposableIncome: number;
    offerId: number;
    physicalAddress: string;
}

export interface AssessmentEMI {
    taxYear: string;
    code: string;
    emiAmount: number;
    remainingMonths: number;
    estimatedStatute: string; // ISO date string
    totalAmount: number
}

export interface AccrualsEMI {
    taxYear: string;
    accruals: number;
    accrualsEMI: number;
    emiMonths: number;
}

export interface InterestDetail {
    interestRate: number;
    totalInterest: number;
    interestEmi: number;
}

export interface PenaltyDetails {
    penaltyRate: number,
    totalPenalty: number,
    penaltyEmi: string
}

interface IUserEligiblePlansState {
    accessToken: string,
    eligiblePlansForUser: EligiblePlansForUser[],
    isJointly: boolean
}

class UserEligiblePlans extends React.Component<{ userId: string, userName: string }, IUserEligiblePlansState> {

    constructor(props: any) {
        super(props);
        this.state = {
            accessToken: '',
            eligiblePlansForUser: [],
            isJointly: false
        }
    }

    async componentDidMount() {
        ///... check token in local storage
        const accesstoken = await loadFromLocalStorage('accessToken');
        ///... if available get user details otherwise re-direct to login page
        if (accesstoken) {
            this.setState({
                accessToken: accesstoken.toString()
            }, () => {
                this.getEligiblePlansForUser()
            });
        }
        else {
            localStorage.clear();
            window.location.href = '/'
        }
    }

    getEligiblePlansForUser = () => {
        ProfileServices.getEligiblePlansForUser({ userId: this.props.userId }, this.state.accessToken).then((r) => {
            if (r.ok) {
                r.json().then(resp => {
                    const results = resp.data.filter((e: EligiblePlansForUser) => e.isEligible && e.isJointly === resp.hasJointTaxpayer).map((plan: EligiblePlansForUser) => plan);

                    this.setState({ eligiblePlansForUser: results, isJointly: resp.hasJointTaxpayer });

                }, () => {
                    ///... post execution once state is set
                }).catch(err => {
                    Swal.fire("Error", err.message ? err.message : 'Failed due to technical difficulties', "error");
                });
            }
        });
    }

    public render() {
        return <>
            <h3 style={{ textAlign: 'center' }}>{this.props.userName}</h3>

            {this.renderPlanSection(this.state.eligiblePlansForUser)}
        </>
    }

    LabelValue = ({
        label,
        value,
    }: {
        label: string;
        value: React.ReactNode;
    }) => (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                py: 0.75,
                gap: 2,
            }}
        >
            <Box sx={{ width: "42%" }}>
                <Typography color="text.secondary" sx={{
                    flex: 1,
                    textAlign: "left",
                }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ width: "58%" }}>
                <Typography fontWeight={500} sx={{
                    flex: 1,
                    textAlign: "right",
                    wordBreak: "break-word",
                }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );

    renderPlanSection = (
        plans: EligiblePlansForUser[]
    ) => {
        return (
            <> {plans.length === 0 ? <Box>There are no eligible plans for this user.</Box> :
                <Grid container spacing={3}>
                    {plans.map((plan) => (
                        <Grid size={{ xs: 12, md: 6 }} key={plan.planId}>
                            <Card sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                border:
                                    plan.recommendation === "Recommended" && plan.isEligible
                                        ? `2px solid var(--secondary-main)`
                                        : "",
                                borderRadius: 2,
                                overflow: "visible",
                                boxShadow: plan.recommendation === "Recommended" && plan.isEligible ? "none" : "1px 1px 5px 1px rgba(0,0,0,0.5)"
                            }}>
                                {(plan.recommendation === "Recommended" && plan.isEligible) && (
                                    <Chip
                                        label="Recommended"
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: -12,
                                            right: 20,
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                            height: 22,
                                            zIndex: 1,
                                            backgroundColor: `var(--secondary-main)`
                                        }}
                                    />
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 2,
                                    }}>
                                        <Box sx={{ width: "12%" }}>
                                            <Box sx={{
                                                color: "var(--primary-main)",
                                                width: 56,
                                                height: 56,
                                                borderRadius: "50%",
                                                backgroundColor: "var(--secondary-main)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}>
                                                <CreditCardIcon sx={{ fontSize: 35 }} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ width: "88%", textAlign: "left" }}>
                                            <Typography variant="h6" sx={{ py: 2 }}>
                                                <strong>{plan.planName}</strong>
                                            </Typography>
                                            {plan.isEligible && (
                                                <>
                                                    <this.LabelValue label="Total Debt :" value={amountFormat(plan.totalDebt, 'en-US', 'USD')} />
                                                    {(plan.planId !== '6' && plan.planId !== '8' && plan.planId !== '9') && (
                                                        // <this.LabelValue label="Total Interest :" value={amountFormat(plan.totalInterest, 'en-US', 'USD')} />
                                                        <this.LabelValue label="Total Interest :" value={(plan.planId === '1') ? amountFormat(plan.totalInterest, 'en-US', 'USD') : amountFormat(plan.interestDetail?.totalInterest ?? 0, 'en-US', 'USD')} />
                                                    )}
                                                    {(plan.planId !== '1' && plan.planId !== '6' && plan.planId !== '8' && plan.planId !== '9') && (
                                                        <this.LabelValue label="Total Penalty :" value={amountFormat(plan.penaltyDetails?.totalPenalty ?? 0, 'en-US', 'USD')} />
                                                    )}
                                                    {(plan.planId !== '6' && plan.planId !== '8' && plan.planId !== '9') && (
                                                        <this.LabelValue label="Amount Payable :" value={amountFormat((plan.planId === '1') ? plan.amountPayable : plan.totalEmiAmountWithAccruals, 'en-US', 'USD')} />
                                                    )}
                                                    {(plan.planId === '6' || plan.planId === '8') && (
                                                        <this.LabelValue label="Disposable Income :" value={amountFormat(plan.disposableIncome, 'en-US', 'USD') ?? 0} />
                                                    )}
                                                    {(plan.planId !== '8' && plan.planId !== '9') && (
                                                        <>
                                                            <this.LabelValue label="Months :" value={(plan.planId == '3' || plan.planId == '5' || plan.planId == '7') ? plan.totalEmiMonths : plan.months} />
                                                            <this.LabelValue label="Payment Type :" value={plan.paymentType ?? 0} />
                                                        </>
                                                    )}
                                                    {(plan.planId === '9') && (
                                                        <>
                                                            <this.LabelValue label="Total Offer Amount :" value={amountFormat(plan.totalOfferAmount, 'en-US', 'USD')} />
                                                            <this.LabelValue label="Payment Terms :" value={plan.offerOption} />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            }
            </>
        );
    };

}

export default UserEligiblePlans;